import React, { useId, useLayoutEffect, useRef } from "react";

/**
 * Labelled field wrapper for forms.
 *
 * Props:
 *   label    – string  — field label text (supports * for required indicator)
 *   children – node    — the input / select / textarea inside
 *   hint     – string  — optional small hint text below the field
 *   htmlFor  – string  — id of the control; links the label and lets
 *                        `useFieldErrors` find/focus the field (the wrapper is
 *                        searched too, so pickers that don't take an id work)
 *   error    – string  — message shown under the field (red outline on the
 *                        control); replaces the hint while set. The error (or
 *                        hint) is linked to the control with aria-describedby,
 *                        and an error sets aria-invalid, so screen readers say it
 *
 * With `useFieldErrors`: `<FormField label="Party *" {...fe.field("party")}>`
 * and put `id={fe.id("party")}` on the control when it accepts one.
 */
function FormField({ label, children, hint, htmlFor, error }) {
    const wrapRef = useRef(null);
    const autoId = useId();
    const baseId = htmlFor || `${autoId}ff`;
    const hintId = `${baseId}-hint`;
    useDescribedBy(hint && !error ? hintId : null, () => findControl(htmlFor, wrapRef.current), false);
    return (
        <div
            ref={wrapRef}
            className={error ? "field-has-error" : undefined}
            data-field-for={htmlFor}
            style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}
        >
            <label htmlFor={htmlFor} style={{ fontSize: "var(--fs-label)", fontWeight: 600, color: "var(--ink-muted)" }}>
                {label}
            </label>
            {children}
            {error ? (
                <FieldError htmlFor={baseId} error={error} inField />
            ) : hint ? (
                <span id={hintId} style={{ fontSize: "var(--fs-hint)", color: "var(--ink-muted)" }}>{hint}</span>
            ) : null}
        </div>
    );
}

// ── Screen-reader wiring ────────────────────────────────────────────────────
// The message is linked to the control from here (aria-describedby, plus
// aria-invalid for errors) instead of through props, so pickers that don't
// forward aria props (DatalistSelect, Combobox, multi-selects) get it too.

const CONTROL = "input:not([type=hidden]), select, textarea, [role=combobox]";

/** The control for a field: #htmlFor, else the first control in its wrapper. */
function findControl(htmlFor, wrap) {
    if (typeof document === "undefined") return null;
    const byId = htmlFor ? document.getElementById(htmlFor) : null;
    if (byId && byId.matches(CONTROL)) return byId;
    const box = wrap
        || (htmlFor && Array.from(document.querySelectorAll("[data-field-for]")).find((w) => w.dataset.fieldFor === htmlFor))
        || byId;
    return box?.querySelector(CONTROL) || null;
}

/** Adds msgId to the control's aria-describedby (and aria-invalid) while mounted. */
function useDescribedBy(msgId, locate, invalid) {
    // No deps: the control can remount under us (e.g. Input re-wraps on error),
    // so re-link after every render; the cleanup undoes exactly what it added.
    useLayoutEffect(() => {
        if (!msgId) return undefined;
        const el = locate();
        if (!el) return undefined;
        const ids = (el.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
        const added = !ids.includes(msgId);
        if (added) el.setAttribute("aria-describedby", [...ids, msgId].join(" "));
        const markInvalid = invalid && !el.hasAttribute("aria-invalid");
        if (markInvalid) el.setAttribute("aria-invalid", "true");
        return () => {
            if (added) {
                const rest = (el.getAttribute("aria-describedby") || "").split(/\s+/).filter((t) => t && t !== msgId);
                if (rest.length) el.setAttribute("aria-describedby", rest.join(" "));
                else el.removeAttribute("aria-describedby");
            }
            if (markInvalid) el.removeAttribute("aria-invalid");
        };
    });
}

/**
 * Just the message line, for fields laid out without <FormField> (put
 * `className={fe.error(name) ? "field-has-error" : ""}` on their wrapper).
 * Don't use <Input error> for this: Input wraps itself in a new element when
 * an error appears, which unmounts the field that was just focused.
 */
export function FieldError({ htmlFor, error, inField = false }) {
    const ref = useRef(null);
    const id = htmlFor ? `${htmlFor}-error` : undefined;
    // Inside <FormField> the wrapper is the message's parent (its own ref
    // isn't attached yet when this child's layout effect runs).
    useDescribedBy(error ? id : null, () => findControl(htmlFor, inField ? ref.current?.parentElement : null), true);
    if (!error) return null;
    return <span ref={ref} id={id} role="alert" className="field-error">{error}</span>;
}

export default FormField;
